import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTable, MatTableModule } from '@angular/material/table';

import { Store } from '@ngrx/store';

import { OrderViewAttachmentsComponent } from '@components/orders/manage-orders/production-flow/order-materials-needed/order-view-attachments/order-view-attachments.component';
import { Observable } from 'rxjs';

import { OrdersActions } from '../../../../../@store/orders/orders.actions';
import {
  Material,
  MaterialAttachment,
  singleOrderDetails,
} from '../../../../../@store/orders/orders.model';
import {
  selectOrderMaterialsCategories,
  selectOrderMaterialsStatus,
} from '../../../../../@store/orders/orders.selectors';
import { QuoteStatus } from '../../../../../@store/quotes/quotes.state.interface';
import { SafeAny } from '../../../../../types/safe-any';

import { OrderAddAttachmentModalComponent } from './order-add-attachment-modal/order-add-attachment-modal.component';

@Component({
  selector: 'ciwc-order-materials-needed',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatDatepickerModule,
    MatCheckboxModule,
  ],
  standalone: true,
  templateUrl: './order-materials-needed.component.html',
  styleUrl: './order-materials-needed.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderMaterialsNeededComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() parentForm!: FormGroup;
  @Input() orderDetails!: singleOrderDetails;
  @Input() viewDisable!: boolean;

  readonly fb = inject(FormBuilder);
  private store = inject(Store);
  private dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);

  dataSource: SafeAny[] = [];
  private lastInitializedOrderId: string | number | null = null;

  @ViewChild(MatTable) table!: MatTable<SafeAny>;

  get materialsArray(): FormArray {
    return this.parentForm.get('materials') as FormArray;
  }

  categories$: Observable<QuoteStatus[]>;
  statuses$: Observable<QuoteStatus[]>;

  displayedColumns = [
    'quantity',
    'description',
    'category',
    'recDate',
    'poNo',
    'status',
    'notes',
    'attachments',
    'actions',
  ];

  constructor() {
    this.categories$ = this.store.select(selectOrderMaterialsCategories);
    this.statuses$ = this.store.select(selectOrderMaterialsStatus);
  }

  ngOnInit(): void {
    if (!this.viewDisable) {
      this.store.dispatch(
        OrdersActions.loadOrderMaterialsCategories({
          payload: { identifier: 'ORDERS_MATERIALS_CATEGORIES' },
        }),
      );
      this.store.dispatch(
        OrdersActions.loadOrderMaterialsStatus({
          payload: { identifier: 'ORDERS_MATERIALS_STATUS' },
        }),
      );
    }
  }

  ngAfterViewInit() {
    if (this.viewDisable && this.materialsArray) {
      this.disableAllControls();
    }
  }

  /**
   * Materials form seeding strategy
   *
   * Context:
   * - Updating unrelated order fields (e.g., notes) triggers store updates that replace `orderDetails`.
   * - Previously, any `orderDetails` change reinitialized the materials form, wiping unsaved user edits.
   *
   * Solution:
   * - Treat the form as the source of truth for materials; only seed from store when appropriate.
   * - Guards added:
   *   1) Order-ID guard: seed materials only when the order ID changes (first load / switching orders).
   *   2) Dirty guard: if the materials form is not pristine (user has edits), skip re-init even if
   *      the store’s materials changed.
   *   3) Structural equality check: `areMaterialsStateEqual(prev, curr)` ensures we only rebuild when
   *      `materials` or `materialsReceived` truly changed, not when unrelated fields (like notes) update.
   *
   * Implementation:
   * - `lastInitializedOrderId`: remembers the order last used to seed the form.
   * - `ngOnChanges` computes:
   *     - `orderIdChanged`: first load or different order.
   *     - `materialsChanged`: real change in materials vs previous orderDetails.
   *     - `isPristine`: whether the form array has no user edits.
   *   We call `initMaterialsForm()` only if:
   *     - `orderIdChanged`, OR
   *     - (`materialsChanged` AND `isPristine`)
   *
   * UX rationale:
   * - Prevents store updates (e.g., add note) from clobbering in-flight materials edits.
   * - Keeps the initial load behavior intact and refreshes materials only when safe.
   * - On submit, payload is built from the form, not the store, aligning with this strategy.
   *
   * Caveat:
   * - If server-side materials change while the user is editing, we intentionally do not overwrite.
   *   Consider prompting the user to refresh or implementing a merge strategy if needed.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['orderDetails'] && this.orderDetails) {
      const prev = changes['orderDetails'].previousValue as singleOrderDetails | undefined;
      const curr = changes['orderDetails'].currentValue as singleOrderDetails;
      const orderIdChanged = this.lastInitializedOrderId === null || this.lastInitializedOrderId !== curr.id;
      const materialsChanged = !prev || !this.areMaterialsStateEqual(prev, curr);
      const existingArray = (this.parentForm.get('materials') as FormArray) || null;
      const isPristine = !existingArray || existingArray.pristine;

      if (orderIdChanged || (materialsChanged && isPristine)) {
        this.initMaterialsForm();
        this.lastInitializedOrderId = curr.id as any;
      }
    }
    if (changes['viewDisable']) {
      const existingArray = (this.parentForm.get('materials') as FormArray) || null;
      if (this.viewDisable && existingArray) {
        this.disableAllControls();
      }
    }
  }

  private areMaterialsStateEqual(a: singleOrderDetails | undefined, b: singleOrderDetails | undefined): boolean {
    if (!a || !b) return false;
    if ((a.materialsReceived ?? null) !== (b.materialsReceived ?? null)) return false;

    const prevMaterials = a.materials ?? [];
    const currMaterials = b.materials ?? [];
    if (prevMaterials.length !== currMaterials.length) return false;

    const normalize = (m: Partial<Material> | undefined) => ({
      quantity: m?.quantity ?? null,
      description: m?.description ?? null,
      category: m?.category ?? null,
      recDate: m?.recDate ? new Date(m.recDate).getTime() : null,
      poNo: m?.poNo ?? null,
      status: m?.status ?? null,
      notes: m?.notes ?? null,
      attachments: (m?.attachments ?? []).map((att: MaterialAttachment) => ({
        id: att?.id ?? null,
        attachmentName: att?.attachmentName ?? null,
        attachmentDocument: att?.attachmentDocument ?? null,
        ordersMaterialsId: att?.ordersMaterialsId ?? null,
        url: att?.url ?? null,
      })),
    });

    for (let i = 0; i < prevMaterials.length; i++) {
      const p = normalize(prevMaterials[i]);
      const c = normalize(currMaterials[i]);
      if (JSON.stringify(p) !== JSON.stringify(c)) return false;
    }
    return true;
  }

  private disableAllControls() {
    this.materialsArray.controls.forEach((control) => {
      control.get('quantity')?.disable();
      control.get('description')?.disable();
      control.get('category')?.disable();
      control.get('recDate')?.disable();
      control.get('poNo')?.disable();
      control.get('status')?.disable();
      control.get('notes')?.disable();
    });
    this.parentForm.get('materialsReceived')?.disable();
  }

  private initMaterialsForm(): void {
    if (this.parentForm.contains('materials')) {
      this.parentForm.removeControl('materials');
    }
    this.parentForm.addControl('materials', this.fb.array([]));

    if (this.parentForm.contains('materialsReceived')) {
      this.parentForm.removeControl('materialsReceived');
    }
    this.parentForm.addControl(
      'materialsReceived',
      this.fb.control(this.orderDetails.materialsReceived),
    );

    if (this.orderDetails?.materials?.length) {
      this.orderDetails.materials
        .filter((mat) => mat && typeof mat === 'object')
        .forEach((mat) => {
          const group = this.createMaterialGroup(mat);
          this.materialsArray.push(group);
        });
    }
    // Always ensure at least one row
    if (this.materialsArray.length === 0) {
      this.addMaterialRow();
    }
    this.updateDataSource();
  }

  createMaterialGroup(mat?: Partial<Material>): FormGroup {
    const attachmentsArray = this.fb.array(
      (mat?.attachments ?? []).map((att: MaterialAttachment) =>
        this.fb.group({
          id: [att.id],
          ordersMaterialsId: [att.ordersMaterialsId],
          attachmentName: [att.attachmentName],
          attachmentDocument: [att.attachmentDocument],
          url: [att.url || ''],
        }),
      ),
    );

    const group = this.fb.group({
      quantity: [mat?.quantity ?? '', [Validators.required]],
      description: [mat?.description ?? '', [Validators.required]],
      category: [mat?.category ?? '', [Validators.required]],
      recDate: [mat?.recDate ? new Date(mat?.recDate) : null],
      poNo: [mat?.poNo ?? ''],
      status: [mat?.status ?? ''],
      notes: [mat?.notes ?? ''],
      attachments: attachmentsArray,
    });

    if (this.viewDisable) {
      group.disable();
    }

    return group;
  }

  addMaterialRow(): void {
    this.materialsArray.push(this.createMaterialGroup());
    this.updateDataSource();
    this.table?.renderRows();
  }

  removeMaterialRow(index: number): void {
    this.materialsArray.removeAt(index);
    this.updateDataSource();
    this.table?.renderRows();
  }

  private updateDataSource() {
    this.dataSource = this.materialsArray.controls;
    this.table?.renderRows();
  }

  trackByIndex(index: number): number {
    return index;
  }

  addAttachment(rowIndex: number): void {
    if (this.viewDisable) return;

    const dialogRef = this.dialog.open(OrderAddAttachmentModalComponent, {
      data: { rowIndex },
      width: '500px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const attachments = this.materialsArray.at(rowIndex).get('attachments') as FormArray;
        attachments.push(
          this.fb.group({
            attachmentName: [result.attachment.attachmentName],
            attachmentDocument: [result.attachment.attachmentDocument],
          }),
        );

        // Mark for check to update the view
        this.cdr.markForCheck();
      }
    });
  }

  viewAttachments(rowIndex: number): void {
    const attachments = (this.materialsArray.at(rowIndex).get('attachments') as FormArray).value;
    const dialogRef = this.dialog.open(OrderViewAttachmentsComponent, {
      data: { attachments: [...attachments] },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.attachments) {
        const attachmentsArray = this.materialsArray.at(rowIndex).get('attachments') as FormArray;
        // Clear and repopulate
        while (attachmentsArray.length) {
          attachmentsArray.removeAt(0);
        }
        result.attachments.forEach((att: any) => {
          attachmentsArray.push(
            this.fb.group({
              attachmentName: [att.attachmentName],
              attachmentDocument: [att.attachmentDocument],
              id: [att.id || null],
            }),
          );
        });

        // Mark for check to update the view
        this.cdr.markForCheck();
      }
    });
  }

  hasAttachments(rowIndex: number): boolean {
    const attachments = this.materialsArray.at(rowIndex).get('attachments') as FormArray;
    return attachments && attachments.length > 0;
  }

  hasUnsavedAttachments(rowIndex: number): boolean {
    const attachments = this.materialsArray.at(rowIndex).get('attachments') as FormArray;
    return attachments && attachments.value.some((att: any) => !att.id);
  }
}
